// SwitchLogs.tsx
import { useState, useEffect, useCallback } from "react";
import {
    Loader2, FileText, Search,
    ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight,
    ArrowUp, ArrowDown, AlertTriangle, Info, Bell, XCircle,
} from "lucide-react";
import baseapi from "../../../services/baseapi";
import { showError } from "../../../utils/toast";

// ── Types ─────────────────────────────────────────────────────────────────────
interface SwitchLog {
    index: number;
    facility: string;
    mnemonic: string;
    severity: string;
    severity_num: string;
    message: string;
    timestamp: string;
}

interface SwitchMeta {
    switch_ip: string;
    switch_name: string;
    asset_id: number;
    poll_time: string;
}

// ── Severity Config ───────────────────────────────────────────────────────────
const SEV_CONFIG: Record<string, { icon: React.ElementType; badge: string; dot: string; row: string }> = {
    warning: {
        icon: AlertTriangle,
        badge: "bg-yellow-500/10 border-yellow-500/20 text-yellow-600 dark:text-yellow-400",
        dot: "bg-yellow-400",
        row: "hover:bg-yellow-500/5",
    },
    error: {
        icon: XCircle,
        badge: "bg-red-500/10 border-red-500/20 text-red-600 dark:text-red-400",
        dot: "bg-red-400",
        row: "hover:bg-red-500/5",
    },
    info: {
        icon: Info,
        badge: "bg-blue-500/10 border-blue-500/20 text-blue-600 dark:text-blue-400",
        dot: "bg-blue-400",
        row: "hover:bg-blue-500/5",
    },
    notice: {
        icon: Bell,
        badge: "bg-green-500/10 border-green-500/20 text-green-600 dark:text-green-400",
        dot: "bg-green-400",
        row: "hover:bg-green-500/5",
    },
};

const getSevCfg = (sev: string) =>
    SEV_CONFIG[sev?.toLowerCase()] ?? {
        icon: Info,
        badge: "bg-slate-100 dark:bg-gray-600/20 border-slate-200 dark:border-gray-600/20 text-slate-700 dark:text-gray-200",
        dot: "bg-gray-400",
        row: "hover:bg-slate-50 dark:hover:bg-gray-700/20",
    };

// Facility color
const FACILITY_COLORS: Record<string, string> = {
    AAA: "text-blue-600 dark:text-blue-400",
    SNMP: "text-red-600 dark:text-red-400",
    COPY: "text-green-600 dark:text-green-400",
    STP: "text-yellow-600 dark:text-yellow-400",
    LINK: "text-purple-600 dark:text-purple-400",
    NT_poe: "text-orange-600 dark:text-orange-400",
    NT_GREEN: "text-cyan-600 dark:text-cyan-400",
};
const getFacilityColor = (f: string) => FACILITY_COLORS[f] ?? "text-slate-600 dark:text-gray-200";

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

// ── Main Component ────────────────────────────────────────────────────────────
export default function SwitchLogs({ asset_id }: { asset_id: any }) {
    const [logs, setLogs] = useState<SwitchLog[]>([]);
    const [meta, setMeta] = useState<SwitchMeta | null>(null);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [sevFilter, setSevFilter] = useState("all");
    const [facFilter, setFacFilter] = useState("all");
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(50);
    const [sort, setSort] = useState<{ key: keyof SwitchLog; dir: "asc" | "desc" }>({
        key: "index", dir: "asc",
    });
    const internal_id = JSON.parse(sessionStorage.getItem("internal_ids") || "{}");
    const client_id = internal_id.client_id;

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const params: any = {};
            if (asset_id) params.asset_id = asset_id;
            params.client_id = client_id;

            const res = await baseapi.get("/switch_logs", { params });
            if (res.data.status) {
                const raw = res.data.data ?? res.data;
                setLogs(raw.logs ?? res.data.logs ?? []);
                setMeta({
                    switch_ip: raw.switch_ip,
                    switch_name: raw.switch_name,
                    asset_id: raw.asset_id,
                    poll_time: raw.poll_time,
                });
            } else showError("Failed to load switch logs");
        } catch (e: any) { showError(e.message); }
        finally { setLoading(false); }
    }, [asset_id]);

    useEffect(() => { fetchData(); }, [fetchData]);

    // Reset page on filter/search change
    useEffect(() => { setPage(1); }, [search, sevFilter, facFilter]);

    // Derived unique facilities
    const facilities = Array.from(new Set(logs.map(l => l.facility).filter(Boolean))).sort();
    const severities = Array.from(new Set(logs.map(l => l.severity).filter(Boolean))).sort();

    // Filter + Search
    const filtered = logs.filter(l => {
        if (sevFilter !== "all" && l.severity?.toLowerCase() !== sevFilter) return false;
        if (facFilter !== "all" && l.facility !== facFilter) return false;
        if (search) {
            const q = search.toLowerCase();
            return (
                l.message?.toLowerCase().includes(q) ||
                l.facility?.toLowerCase().includes(q) ||
                l.mnemonic?.toLowerCase().includes(q)
            );
        }
        return true;
    });

    // Sort
    const sorted = [...filtered].sort((a, b) => {
        const av = a[sort.key];
        const bv = b[sort.key];
        const cmp = typeof av === "number" && typeof bv === "number"
            ? av - bv
            : String(av ?? "").localeCompare(String(bv ?? ""));
        return sort.dir === "asc" ? cmp : -cmp;
    });

    // Paginate
    const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
    const safePage = Math.min(page, totalPages);
    const pageItems = sorted.slice((safePage - 1) * pageSize, safePage * pageSize);
    const start = sorted.length === 0 ? 0 : (safePage - 1) * pageSize + 1;
    const end = Math.min(safePage * pageSize, sorted.length);

    const handleSort = (key: keyof SwitchLog) => {
        setSort(prev => prev.key === key
            ? { key, dir: prev.dir === "asc" ? "desc" : "asc" }
            : { key, dir: "asc" }
        );
        setPage(1);
    };

    const SortIcon = ({ col }: { col: keyof SwitchLog }) =>
        sort.key !== col
            ? <ArrowUp size={9} className="opacity-0 group-hover:opacity-30 transition-opacity text-slate-400 dark:text-gray-200" />
            : sort.dir === "asc"
                ? <ArrowUp size={9} className="text-blue-500 dark:text-blue-400" />
                : <ArrowDown size={9} className="text-blue-500 dark:text-blue-400" />;

    const Th = ({ label, col, cls = "" }: { label: string; col: keyof SwitchLog; cls?: string }) => (
        <th
            onClick={() => handleSort(col)}
            className={`px-3 py-2 text-left text-[10px] font-bold text-slate-500 dark:text-gray-200 uppercase tracking-widest cursor-pointer whitespace-nowrap group select-none ${cls}`}
        >
            <div className="flex items-center gap-1">
                {label} <SortIcon col={col} />
            </div>
        </th>
    );

    return (
        <div className="flex flex-col gap-3">


            {/* Filters */}
            <div className="flex items-center gap-2 flex-wrap">
                {/* Search */}
                <div className="relative group">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-400 dark:text-gray-200 group-focus-within:text-blue-500 dark:group-focus-within:text-blue-400 transition-colors" />
                    <input
                        type="text"
                        value={search}
                        onChange={e => { setSearch(e.target.value); }}
                        placeholder="Search message, facility, mnemonic..."
                        className="w-56 bg-slate-50 dark:bg-gray-900 border border-slate-200 dark:border-gray-700 rounded-lg pl-7 pr-3 py-1.5 text-[11px] text-gray-900 dark:text-white placeholder-slate-400 focus:outline-none focus:border-blue-500/50 transition-all"
                    />
                </div>

                {/* Severity Filter */}
                <div className="flex items-center gap-1 bg-slate-100 dark:bg-gray-800/60 border border-slate-200 dark:border-gray-700 rounded-lg p-0.5">
                    <button
                        onClick={() => setSevFilter("all")}
                        className={`px-2.5 py-1 rounded-md text-[9px] font-black uppercase tracking-widest transition-all ${sevFilter === "all" ? "bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm" : "text-slate-500 dark:text-gray-200 hover:text-gray-900 dark:hover:text-gray-300"}`}
                    >
                        All
                    </button>
                    {severities.map(s => {
                        const cfg = getSevCfg(s);
                        const Icon = cfg.icon;
                        return (
                            <button
                                key={s}
                                onClick={() => setSevFilter(s)}
                                className={`flex items-center gap-1 px-2.5 py-1 rounded-md text-[9px] font-black uppercase tracking-widest transition-all ${sevFilter === s
                                    ? `border bg-white dark:bg-transparent ${cfg.badge}`
                                    : "text-slate-500 dark:text-gray-200 hover:text-gray-900 dark:hover:text-gray-300"
                                    }`}
                            >
                                <Icon size={9} />
                                {s.replace(/_/g, ' ')}
                            </button>
                        );
                    })}
                </div>

                {/* Facility Filter */}
                <select
                    value={facFilter}
                    onChange={e => setFacFilter(e.target.value)}
                    className="bg-slate-50 dark:bg-gray-900 border border-slate-200 dark:border-gray-700 rounded-lg px-2.5 py-1.5 text-[10px] font-bold text-gray-700 dark:text-gray-300 focus:outline-none focus:border-blue-500/50 uppercase tracking-widest cursor-pointer"
                >
                    <option value="all">All Facilities</option>
                    {facilities.map(f => (
                        <option key={f} value={f}>{f.replace(/_/g, ' ')}</option>
                    ))}
                </select>

                {/* Clear */}
                {(search || sevFilter !== "all" || facFilter !== "all") && (
                    <button
                        onClick={() => { setSearch(""); setSevFilter("all"); setFacFilter("all"); }}
                        className="text-[9px] text-blue-600 dark:text-blue-400 hover:text-blue-500 font-bold uppercase tracking-widest transition-colors"
                    >
                        Clear
                    </button>
                )}

                <div className="text-slate-500 dark:text-gray-200 text-xs font-bold">
                    Polled: {
                        meta?.poll_time
                            ? new Date(meta.poll_time + "Z").toLocaleString()
                            : "-"
                    }
                </div>
            </div>

            {/* Table */}
            {loading ? (
                <div className="flex items-center justify-center gap-2 py-16">
                    <Loader2 className="w-4 h-4 animate-spin text-blue-500 dark:text-blue-400" />
                    <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500 dark:text-gray-200">Loading logs...</span>
                </div>
            ) : sorted.length === 0 ? (
                <div className="flex flex-col items-center gap-2 py-16 opacity-40">
                    <FileText size={22} className="text-slate-400 dark:text-gray-200" />
                    <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500 dark:text-gray-200">No logs found</span>
                </div>
            ) : (
                <div className="bg-white dark:bg-gray-800/40 border border-slate-200 dark:border-gray-700 rounded-xl overflow-hidden shadow-sm">
                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse">
                            <thead className="bg-slate-50 dark:bg-gray-800/80 sticky top-0 z-10">
                                <tr className="border-b border-slate-200 dark:border-gray-700/60">
                                    <Th label="#" col="index" />
                                    <Th label="Timestamp" col="timestamp" />
                                    <Th label="Severity" col="severity" />
                                    <Th label="Facility" col="facility" />
                                    <Th label="Mnemonic" col="mnemonic" />
                                    <Th label="Message" col="message" />
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 dark:divide-gray-700/30">
                                {pageItems.map((log, i) => {
                                    const sevCfg = getSevCfg(log.severity);
                                    const SevIcon = sevCfg.icon;
                                    return (
                                        <tr
                                            key={`${log.index}-${i}`}
                                            className={`transition-colors ${sevCfg.row}`}
                                        >
                                            {/* Index */}
                                            <td className="px-3 py-1.5 whitespace-nowrap">
                                                <span className="text-[10px] font-mono text-slate-600 dark:text-gray-200">{log.index}</span>
                                            </td>

                                            {/* Timestamp */}
                                            <td className="px-3 py-1.5 whitespace-nowrap">
                                                <span className="text-[10px] font-mono text-slate-600 dark:text-gray-200">{log.timestamp || "—"}</span>
                                            </td>

                                            {/* Severity */}
                                            <td className="px-3 py-1.5 whitespace-nowrap">
                                                {log.severity ? (
                                                    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[8px] font-black border uppercase ${sevCfg.badge}`}>
                                                        <SevIcon size={8} />
                                                        {log.severity}
                                                    </span>
                                                ) : <span className="text-slate-400 dark:text-gray-200 text-[9px]">—</span>}
                                            </td>

                                            {/* Facility */}
                                            <td className="px-3 py-1.5 whitespace-nowrap">
                                                <span className={`text-[10px] font-black ${getFacilityColor(log.facility)}`}>
                                                    {log.facility.replace(/_/g, ' ') || "—"}
                                                </span>
                                            </td>

                                            {/* Mnemonic */}
                                            <td className="px-3 py-1.5 whitespace-nowrap">
                                                <span className="text-[10px] font-mono text-slate-700 dark:text-gray-200 bg-slate-100 dark:bg-gray-800/60 px-1.5 py-0.5 rounded border border-slate-200 dark:border-gray-700/50">
                                                    {log.mnemonic || "—"}
                                                </span>
                                            </td>

                                            {/* Message */}
                                            <td className="px-3 py-1.5">
                                                <p className="text-[10px] text-slate-700 dark:text-gray-300 leading-snug line-clamp-2">
                                                    {log.message || "—"}
                                                </p>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination Footer */}
                    <div className="flex items-center justify-between px-3 py-2 border-t border-slate-200 dark:border-gray-700/60 bg-slate-50 dark:bg-gray-800/40 flex-wrap gap-2">
                        <div className="flex items-center gap-3">
                            {/* Page size */}
                            <div className="flex items-center gap-1.5">
                                <span className="text-[9px] text-slate-500 dark:text-gray-200 uppercase tracking-widest">Rows</span>
                                <select
                                    value={pageSize}
                                    onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}
                                    className="bg-white dark:bg-gray-900 border border-slate-200 dark:border-gray-700 rounded px-1.5 py-0.5 text-[10px] text-gray-700 dark:text-gray-300 focus:outline-none"
                                >
                                    {PAGE_SIZE_OPTIONS.map(n => (
                                        <option key={n} value={n}>{n}</option>
                                    ))}
                                </select>
                            </div>
                            <span className="text-[9px] text-slate-500 dark:text-gray-200">
                                <span className="text-gray-700 dark:text-gray-300">{start}</span>
                                {" – "}
                                <span className="text-gray-700 dark:text-gray-300">{end}</span>
                                <span className="mx-1 text-slate-400 dark:text-gray-200">/</span>
                                <span className="text-gray-700 dark:text-gray-300 font-bold">{sorted.length}</span>
                                {sorted.length !== logs.length && (
                                    <span className="text-slate-400 dark:text-gray-200 ml-1">(filtered from {logs.length})</span>
                                )}
                            </span>
                        </div>

                        {/* Page buttons */}
                        <div className="flex items-center gap-1">
                            <button disabled={safePage === 1} onClick={() => setPage(1)}
                                className="p-1 text-slate-500 dark:text-gray-200 hover:text-gray-900 dark:hover:text-white disabled:opacity-20 bg-white dark:bg-gray-800/50 hover:bg-slate-100 dark:hover:bg-gray-700 border border-slate-200 dark:border-transparent rounded transition-all">
                                <ChevronsLeft size={13} />
                            </button>
                            <button disabled={safePage === 1} onClick={() => setPage(p => p - 1)}
                                className="p-1 text-slate-500 dark:text-gray-200 hover:text-gray-900 dark:hover:text-white disabled:opacity-20 bg-white dark:bg-gray-800/50 hover:bg-slate-100 dark:hover:bg-gray-700 border border-slate-200 dark:border-transparent rounded transition-all">
                                <ChevronLeft size={13} />
                            </button>
                            <span className="px-2 py-0.5 bg-blue-500/10 border border-blue-500/20 text-blue-600 dark:text-blue-400 rounded text-[10px] font-bold mx-1 min-w-[28px] text-center">
                                {safePage}
                            </span>
                            <span className="text-[9px] text-slate-500 dark:text-gray-200">/ {totalPages}</span>
                            <button disabled={safePage >= totalPages} onClick={() => setPage(p => p + 1)}
                                className="p-1 text-slate-500 dark:text-gray-200 hover:text-gray-900 dark:hover:text-white disabled:opacity-20 bg-white dark:bg-gray-800/50 hover:bg-slate-100 dark:hover:bg-gray-700 border border-slate-200 dark:border-transparent rounded transition-all">
                                <ChevronRight size={13} />
                            </button>
                            <button disabled={safePage >= totalPages} onClick={() => setPage(totalPages)}
                                className="p-1 text-slate-500 dark:text-gray-200 hover:text-gray-900 dark:hover:text-white disabled:opacity-20 bg-white dark:bg-gray-800/50 hover:bg-slate-100 dark:hover:bg-gray-700 border border-slate-200 dark:border-transparent rounded transition-all">
                                <ChevronsRight size={13} />
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
